import { isNumber } from 'lodash-es'

// These are error codes for submitting an order. I don't know that they are
// super consistent across services ಠ_ಠ. Before using this for other services
// the codes should be verified.
const ORDER_ISSUE_CODES = {
  FULFILLMENT_RESTRICTION: 110,
  NO_SHIP_METHOD: 111,
  OUT_OF_STOCK: 114,
  PARTIALLY_OUT_OF_STOCK: 115,
  NO_EAD_MISSING_ZIPCODE_CONFIG: 118, // "This zip code was not configured in CSA or zip code does not exist
  NO_EAD_LOCATION_CONFIG_ERROR: 119, // "This store location has an incorrect setting in BY
  DUPLICATE_GC: 213, // Duplicate gift card code
  INSUFFICIENT_TENDER_AMOUNT: 302,
  NO_VALID_TENDERS: 303,
  PAYPAL_DISABLED: 304,
  PAYPAL_TIMEOUT: 307,
  PAYPAL_INTERNAL_ERROR: 309,
  ONE_TIME_USE_GC: 316,
  PAYPAL_BAD_REQUEST: 326,
  PAYPAL_EXPIRED: 327,
  ALREADY_A_MEMBER: 503, // "A primary member cannot buy a membership"
  EMPLOYEE_NAME_MISMATCH: 550, // Employee name on record does not match billing address name
  EMPLOYEE_TERMINATED: 551, // "Employee has been terminated"
  EMPLOYEE_CANNOT_BE_VALIDATED: 552, // "Employee information could not be validated."
  INCOMPLETE_BILLING_INFORMATION: 600, // "Incomplete Billing Information"
  INVALID_BILL_ADDRESS_CHARS: 601,
  INCOMPLETE_SHIPPING_INFORMATION: 700, // "Line Item(s) With Incomplete Shipping Information"
  INVALID_SHIP_ADDRESS_CHARS: 701,
  SHIPPING_METHOD_CONFLICTS_WITH_PICKUP: 706, // "Conflicting lines for international shipping and pickup"
  NO_ELIGIBLE_LOCATIONS_BY_SRP: 709, // "No shipping location available for address"


}

const ERROR_CODES = {
  ORDER_LIMIT: 3507, // Order has reached line item limit
  INVALID_REQUEST: 10001, // "Invalid field value in the request"
  INVALID_COOKIE: 10002,
  APPLE_PAY_FEATURE_DISABLED: 10050, // Apple Pay feature is disabled
  APPLE_PAY_INVALID_MERCHANT_REQUEST: 10051, // Apple Pay Merchant Request is invalid
  APPLE_PAY_MERCHANT_FAILURE: 10052, // Apple Pay Merchant system unavailable
  OP_ORDER_NOT_FOUND: 10100, // "Order does not exist in OP Checkout for that id"
  DUPLICATE_TENDERS: 20203, // "Duplicate tenders found"
  CREDIT_CARD_SYSTEM_FAILURE: 20300, // "System error from PySH"
  CREDIT_CARD_DECLINED_FAILURE: 20301, // "Credit card has been declined."
  CREDIT_CARD_NUMBER_INVALID: 20302, // "Credit card number is invalid"
  CREDIT_CARD_EXPIRATION_INVALID: 20303, // "Credit card expiration date is invalid"
  CREDIT_CARD_PIN_INVALID: 20304, // "Credit card pin is invalid"
  CREDIT_CARD_TYPE_INVALID: 20305, // "Credit card type is invalid"
  GIFT_CARD_INVALID: 20402, // "Gift card type invalid"
  PYSH_PAYPAL_ERROR: 20502, // The PayPal request returned an error.
  PYSH_PAYPAL_TOKEN_EXPIRED_ERROR: 20503, // The PayPal token has expired.
  PYSH_PAYPAL_NOT_CONSENTED_ERROR: 20504, // Buyer has not consented PayPal purchase.
  PYSH_PAYPAL_AUTHORIZATION_ERROR: 20505, // Authorization of the PayPal tender failed.
  ILA_OVER_CREDIT_LIMIT: 20508,
  CREDIT_CARD_AUTHORIZATION_FAILURE: 30200,
  ORDER_CANNOT_BE_SUBMITTED: 30201, // Order Issues must be resolved
}

const has = (array, matchf) => (Array.isArray(array))
  ? array.some(matchf)
  : false

class Errors {
  constructor(errors) {
    this.errors = errors || []
  }

  hasCode(code) {
    const matchSingleCode = e => e.code === code
    const matchAnyCode = e => code.some(c => c === e.code)
    const match = (Array.isArray(code)) ? matchAnyCode : matchSingleCode
    return has(this.errors, match)
  }

  isOverIlaLimit() {
    return this.hasCode([
      ERROR_CODES.ILA_OVER_CREDIT_LIMIT,
    ])
  }

  hasUnavailableItems() {
    return this.hasCode([
      ORDER_ISSUE_CODES.OUT_OF_STOCK,
      ORDER_ISSUE_CODES.PARTIALLY_OUT_OF_STOCK,
    ])
  }

  hasPayPalError() {
    return this.hasCode([
      ORDER_ISSUE_CODES.PAYPAL_EXPIRED,
      ORDER_ISSUE_CODES.PAYPAL_DISABLED,
      ERROR_CODES.PYSH_PAYPAL_ERROR,
      ERROR_CODES.PYSH_PAYPAL_TOKEN_EXPIRED_ERROR,
      ERROR_CODES.PYSH_PAYPAL_NOT_CONSENTED_ERROR,
      ERROR_CODES.PYSH_PAYPAL_AUTHORIZATION_ERROR,
    ])
  }

  hasCreditCardProcessingError() {
    return this.hasCode([
      ERROR_CODES.CREDIT_CARD_AUTHORIZATION_FAILURE,
      ERROR_CODES.CREDIT_CARD_DECLINED_FAILURE,
      ERROR_CODES.CREDIT_CARD_NUMBER_INVALID,
      ERROR_CODES.CREDIT_CARD_EXPIRATION_INVALID,
      ERROR_CODES.CREDIT_CARD_PIN_INVALID,
    ])
  }

  hasDuplicateTenderError() {
    return this.hasCode([
      ERROR_CODES.DUPLICATE_TENDERS,
    ])
  }

  hasOrderLimitError() {
    return this.hasCode([
      ERROR_CODES.ORDER_LIMIT,
    ])
  }

  isApplePayDisabled() {
    return this.hasCode([
      ERROR_CODES.APPLE_PAY_FEATURE_DISABLED,
    ])
  }

  hasApplePayError() {
    return this.hasCode([
      ERROR_CODES.APPLE_PAY_INVALID_MERCHANT_REQUEST,
      ERROR_CODES.APPLE_PAY_MERCHANT_FAILURE,
    ])
  }


  static get codes() { return ERROR_CODES }
}

class OrderIssues {
  constructor(issues) {
    this.issues = issues || []
  }

  hasCode(code) {
    const matchSingleCode = i => i.code === code
    const matchAnyCode = i => code.some(c => c === i.code)
    const match = (Array.isArray(code)) ? matchAnyCode : matchSingleCode
    return has(this.issues, match)
  }

  hasUnavailableItems() {
    return this.hasCode([ORDER_ISSUE_CODES.OUT_OF_STOCK, ORDER_ISSUE_CODES.PARTIALLY_OUT_OF_STOCK])
  }

  hasNoShipMethod() {
    return this.hasCode(ORDER_ISSUE_CODES.NO_SHIP_METHOD)
  }

  hasNoShipAddress() {
    return this.hasCode(ORDER_ISSUE_CODES.INCOMPLETE_SHIPPING_INFORMATION)
  }

  hasInvaildShipAddressCharacters() {
    return this.hasCode(ORDER_ISSUE_CODES.INVALID_SHIP_ADDRESS_CHARS)
  }

  hasInvaildBillingAddressCharacters() {
    return this.hasCode(ORDER_ISSUE_CODES.INVALID_BILL_ADDRESS_CHARS)
  }

  isPayPalExpired() {
    return this.hasCode(ORDER_ISSUE_CODES.PAYPAL_EXPIRED)
  }

  isPayPalDisabled() {
    return this.hasCode(ORDER_ISSUE_CODES.PAYPAL_DISABLED)
  }

  isAlreadyAMember() {
    return this.hasCode(ORDER_ISSUE_CODES.ALREADY_A_MEMBER)
  }

  extractBYErrorDescription(errorDescription){
    const match = errorDescription.match(/\(([^)]+)\)/);
    return match ? match[1] : null;
  }

  hasFulfillmentRestrictions () {
    return this.hasCode( ORDER_ISSUE_CODES.FULFILLMENT_RESTRICTION )
      || this.hasCode( ORDER_ISSUE_CODES.NO_ELIGIBLE_LOCATIONS_BY_SRP )
      || this.hasCode( ORDER_ISSUE_CODES.NO_EAD_MISSING_ZIPCODE_CONFIG )
      || this.hasCode(ORDER_ISSUE_CODES.NO_EAD_LOCATION_CONFIG_ERROR)
  }

  hasNoLocationBySRPBlueYonderRestriction () {
    return this.hasCode( ORDER_ISSUE_CODES.NO_ELIGIBLE_LOCATIONS_BY_SRP );
  }

  hasEADConfigBlueYonderRestriction () {
    return this.hasCode( ORDER_ISSUE_CODES.NO_EAD_MISSING_ZIPCODE_CONFIG );
  }

  hasBlueYonderStoreConfigRestriction () {
    return this.hasCode( ORDER_ISSUE_CODES.NO_EAD_LOCATION_CONFIG_ERROR );
  }

  hasIncompleteBillingAddress() {
    return this.hasCode(ORDER_ISSUE_CODES.INCOMPLETE_BILLING_INFORMATION)
  }

  hasShippingAddressConflictsWithPickup() {
    return this.hasCode(ORDER_ISSUE_CODES.SHIPPING_METHOD_CONFLICTS_WITH_PICKUP)
  }

  isOneTimeUseGC() {
    return this.hasCode(ORDER_ISSUE_CODES.ONE_TIME_USE_GC)
  }

  isAlreadyAppliedGC() {
    return this.hasCode(ORDER_ISSUE_CODES.DUPLICATE_GC)
  }

  hasNoTenders() {
    return this.hasCode([
      ORDER_ISSUE_CODES.NO_VALID_TENDERS,
      ORDER_ISSUE_CODES.INSUFFICIENT_TENDER_AMOUNT,
    ])
  }

  /**
    Finds the first order issue for the provided code if one exists
    @param {number} code a order issue code specifying the issue type to find
   */
  findIssueForCode(code) {
    if (!isNumber(code)) throw new Error('method `findIssueForCode` requires argument `code` to be of type number')

    return this.issues.find(i => i.code === code)
  }

  static get codes() { return ORDER_ISSUE_CODES }
}

export { OrderIssues, Errors }
